// A2F-547: Shared signup submit flow.
//
// Extracted from SignupModal.tsx so the Modal (still used by /install for
// the Batch 2 cross-device recovered-profile path, per F7) and the new
// inline SignupForm on / (A2F-546) both consume the same logic. The
// extraction is behavior-preserving — validation, localStorage writes,
// the pending-profile backend write (A2F-538), the api.signup POST,
// and telemetry all keep their existing shape.
//
// The hook returns:
//   submit(input)      — call with form values; no-op if already submitting
//   phase              — 'form' | 'submitting' | 'sent'
//   error              — human-readable error string when validation or
//                        network fails (localized at the callsite)
//   reset()            — returns phase to 'form' and clears error
//
// Callers own the form state; the hook owns only phase/error transitions.

import { useCallback, useState } from 'react'
import * as api from './api'
import {
  persistPendingProfile,
  persistPendingTier,
  persistPendingMetadata,
  type PendingMetadata,
  type TierChoice,
} from '../components/SignupModal'
import { Telemetry } from './telemetry'
import type { Profile } from '../types'

export type SignupPhase = 'form' | 'submitting' | 'sent'

export interface SignupInput {
  email: string
  password: string
  displayName?: string
  organization?: string
  roleTitle?: string
  tier: TierChoice
  terms: boolean
  profile: Profile
}

export interface UseSignupResult {
  phase: SignupPhase
  error: string | null
  submit: (input: SignupInput) => Promise<void>
  reset: () => void
}

// Kept aligned with SignupModal's original hard-coded date. Bumping this
// is a SignupModal/useSignup coordinated change — the backend schema
// requires this exact string on the signup body and on the
// pending-profile body, and the two must agree.
const TERMS_VERSION = '2026-04-16'

export function useSignup(): UseSignupResult {
  const [phase, setPhase] = useState<SignupPhase>('form')
  const [error, setError] = useState<string | null>(null)

  const reset = useCallback(() => {
    setPhase('form')
    setError(null)
  }, [])

  const submit = useCallback(async (input: SignupInput) => {
    setError(null)

    // Batch 4.5: corr_A is the pre-confirm half of the correlation
    // handoff (Option C). One uuid spans both putPendingProfile and
    // the auth/signup POST so Railway logs for the pre-confirm chain
    // grep cleanly. The post-confirm half (corr_B) is minted
    // independently by InstallPage — the server-side anchor stored
    // on pending_profiles.correlation_id is what joins the two.
    const correlationId = crypto.randomUUID()

    if (input.password.length < 8) {
      setError('Password must be at least 8 characters.')
      return
    }

    // A2F-540 optional-field validation: empty OR ≥ 2 chars.
    const trimmedName = (input.displayName ?? '').trim()
    const trimmedOrg = (input.organization ?? '').trim()
    const trimmedRole = (input.roleTitle ?? '').trim()
    if (trimmedName && trimmedName.length < 2) {
      setError('Display name must be at least 2 characters.')
      return
    }
    if (trimmedOrg && trimmedOrg.length < 2) {
      setError('Organization must be at least 2 characters.')
      return
    }
    if (trimmedRole && trimmedRole.length < 2) {
      setError('Role must be at least 2 characters.')
      return
    }
    if (!input.terms) {
      setError('Please accept the terms to continue.')
      return
    }

    // Email wins when the optional field is blank; backend requires
    // min_length:1 for each of display_name / organization_name /
    // role_title, so the email fallback preserves chunks-1-5 behavior
    // and lets a later "complete your profile" prompt detect the
    // default cheaply.
    const effectiveDisplayName = trimmedName || input.email
    const effectiveOrganization = trimmedOrg || input.email
    const effectiveRoleTitle = trimmedRole || input.email
    const metadata: PendingMetadata = {
      displayName: trimmedName || undefined,
      organization: trimmedOrg || undefined,
      roleTitle: trimmedRole || undefined,
    }

    persistPendingProfile(input.profile)
    persistPendingTier(input.tier)
    persistPendingMetadata(metadata)
    setPhase('submitting')

    // signup_started fires with just the tier; email is NEVER a property
    // (PII). signup_completed fires on InstallPage after confirm.
    Telemetry.configuratorSignupStarted({ tier_choice: input.tier })

    // A2F-538 cross-device recovery: pre-save the profile server-side
    // keyed on a peppered email hash. Best-effort — a failure here
    // doesn't block signup because the localStorage copy still covers
    // the same-device path.
    try {
      await api.putPendingProfile(
        {
          email: input.email,
          profile: input.profile,
          tier_choice: input.tier,
          terms_version: TERMS_VERSION,
          signup_metadata:
            trimmedName || trimmedOrg || trimmedRole
              ? {
                  display_name: trimmedName || undefined,
                  organization_name: trimmedOrg || undefined,
                  role_title: trimmedRole || undefined,
                }
              : undefined,
        },
        { correlationId },
      )
    } catch (err) {
      console.warn(
        'pending-profile write failed; cross-device recovery may not work',
        err,
      )
    }

    try {
      await api.signup(
        {
          email: input.email,
          password: input.password,
          display_name: effectiveDisplayName,
          organization_name: effectiveOrganization,
          role_title: effectiveRoleTitle,
          terms_version: TERMS_VERSION,
          source: 'chrona_pro',
        },
        { correlationId },
      )
      // Batch 4.5: onboarding_signup_submitted carries correlation_id
      // as a PostHog event property so funnel positions join cleanly
      // with Railway log lines (F8). Email is never a property.
      Telemetry.onboardingSignupSubmitted({
        tier: input.tier,
        has_display_name: !!trimmedName,
        has_organization: !!trimmedOrg,
        has_role_title: !!trimmedRole,
        correlation_id: correlationId,
      })
      setPhase('sent')
    } catch (e) {
      Telemetry.onboardingSignupFailed({
        tier: input.tier,
        reason: e instanceof Error ? 'network' : 'unknown',
        correlation_id: correlationId,
      })
      // Backend is email-enumeration-resistant — a real failure here is
      // almost always network or backend.
      setError(
        e instanceof Error
          ? "Couldn't reach Chrona. Check your connection and try again."
          : 'Unknown error.',
      )
      setPhase('form')
    }
  }, [])

  return { phase, error, submit, reset }
}
